import { FormFieldProps, FormFieldValue } from '../Form.types';
import { isBusinessEmail } from './businessEmail';
import { getValidationMessage } from './localization';
import {
  BASE_FIELDS,
  COMPANY,
  DEMO_PRODUCT_FORM_TYPE,
  EMAIL,
  EMAIL_ANY_DOMAIN,
  EMAIL_REGEX,
  FIRST_NAME,
  JOB_TITLE,
  LAST_NAME,
  MEDIA_OUTLET,
  MOBILE_PHONE,
  PHONE_REGEX,
  PRODUCT_INTEREST,
  QUESTION,
  TYPE_OF_REQUEST,
  UNSUBSCRIBE_FORM_TYPE,
} from './constants';

export type FieldValidator = (value: FormFieldValue, locale?: string, field?: FormFieldProps) => string | undefined;

// Required-ness and max length are validated generically from the field definition;
// only fields with bespoke rules need a validator here.
export const fieldValidators: Record<string, FieldValidator> = {
  email: (value: FormFieldValue, locale?: string, field?: FormFieldProps) => {
    if (!value) return undefined;
    if (!EMAIL_REGEX.test(value)) return getValidationMessage('invalidEmail', locale);
    // Format is checked on every email field; the business-domain rule is the part a field
    // can opt out of, so both rules stay in one validator rather than splitting by field.
    if (!field?.allowConsumerEmail && !isBusinessEmail(value.toLowerCase())) {
      return getValidationMessage('nonBusinessEmail', locale);
    }
    return undefined;
  },
  phone: (value: FormFieldValue, locale?: string) => {
    if (!value) return undefined;
    if (!PHONE_REGEX.test(value)) return getValidationMessage('invalidPhone', locale);
    return undefined;
  },
};

// Each list mirrors the field order in the requirements table for that form type.
const FIELDS_BY_TYPE: Record<string, FormFieldProps[]> = {
  demo_generic: [FIRST_NAME, LAST_NAME, COMPANY, JOB_TITLE, EMAIL, MOBILE_PHONE, PRODUCT_INTEREST],
  [DEMO_PRODUCT_FORM_TYPE]: [FIRST_NAME, LAST_NAME, COMPANY, JOB_TITLE, { ...EMAIL, fullWidth: true }],
  content_download: BASE_FIELDS,
  webinar_registration: BASE_FIELDS,
  on_demand_webinar: BASE_FIELDS,
  press: [FIRST_NAME, LAST_NAME, EMAIL, TYPE_OF_REQUEST, MEDIA_OUTLET, QUESTION],
  // Phone is required here, unlike on demo_generic.
  live_event: [FIRST_NAME, LAST_NAME, COMPANY, JOB_TITLE, EMAIL, { ...MOBILE_PHONE, isRequired: true }],
  newsletter_subscribe: [FIRST_NAME, LAST_NAME, { ...EMAIL, fullWidth: true }],
  [UNSUBSCRIBE_FORM_TYPE]: [{ ...EMAIL_ANY_DOMAIN, fullWidth: true }],
};

const FormTypeMapping: Record<string, string> = {
  'Demo Generic': 'demo_generic',
  'Demo Product': DEMO_PRODUCT_FORM_TYPE,
  'Content Download': 'content_download',
  'Webinar Registration': 'webinar_registration',
  'On Demand Webinar': 'on_demand_webinar',
  Press: 'press',
  'Live Event': 'live_event',
  'Newsletter Subscribe': 'newsletter_subscribe',
  Unsubscribe: UNSUBSCRIBE_FORM_TYPE,
};

/** Normalize the Contentful display value (e.g. "Demo Generic") to its internal form type key. */
export const resolveFormType = (type?: string): string | undefined =>
  type ? FormTypeMapping[type] || type : undefined;

/**
 * @param productInterest the Form entry's value, appended as a hidden field on demo_product forms.
 *   Those sit on product-specific pages, so they ask no Product Interest question and the editor
 *   pins the answer instead. Passed through as authored — Contentful allows products the dropdown
 *   does not offer, so checking it against PRODUCT_INTEREST_OPTIONS would drop exactly those.
 */
export const getFieldsForType = (type?: string, productInterest?: string): FormFieldProps[] => {
  const formType = resolveFormType(type);
  const fields = (formType && FIELDS_BY_TYPE[formType]) || BASE_FIELDS;
  const pinned = productInterest?.trim();

  // demo_product only: every other type either renders the visible dropdown, where a second
  // control of the same name would make FormData.get() return whichever comes first, or asks for
  // no product interest at all.
  if (formType !== DEMO_PRODUCT_FORM_TYPE || !pinned) return fields;

  return [
    ...fields,
    { id: 'product-interest-hidden', fieldName: 'productInterest', inputType: 'hidden', value: pinned },
  ];
};
